import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search as SearchIcon, MapPin, Calendar, Clock } from 'lucide-react';
import { getFilteredEstablishments } from '../helpers/establishments';
import { getEvents } from '../helpers/events';
import { API_BASE_URL } from '../helpers/api';
import '../css/Search.css';

const Search = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [spots, setSpots] = useState([]);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const fetchResults = async () => {
      if (searchTerm.length === 0) {
        setSpots([]);
        setEvents([]);
        return;
      }

      try {
        setIsLoading(true);
        
        // Fetch both spots and events in parallel
        const [spotsResponse, eventsData] = await Promise.all([
          getFilteredEstablishments({
            search: searchTerm,
            page: 1,
            pageSize: 3  // Reduced to make room for events
          }),
          getEvents()
        ]);

        // Filter events manually
        const searchTermLower = searchTerm.toLowerCase();
        const filteredEvents = eventsData
          .filter(event => 
            event.name.toLowerCase().includes(searchTermLower) ||
            event.description.toLowerCase().includes(searchTermLower) ||
            (event.venueName && event.venueName.toLowerCase().includes(searchTermLower))
          )
          .slice(0, 3); // Limit to 3 events

        setSpots(spotsResponse.data);
        setEvents(filteredEvents);
      } catch (error) {
        console.error('Search error:', error);
        setSpots([]);
        setEvents([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce the search
    const timeoutId = setTimeout(fetchResults, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const formatEventDate = (dateString) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long'
    }).format(new Date(dateString));
  };

  const formatEventTime = (dateString) => {
    return new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleResultClick = (type, id) => {
    navigate(`/${type}/${id}`);
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="search-overlay" onKeyDown={handleKeyDown} onClick={handleOverlayClick}>
      <div className="search-modal">
        <div className="search-header">
          <div className="search-input-wrapper">
            <SearchIcon size={20} />
            <input
              type="text"
              placeholder="Rechercher un spot ou un événement..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="search-content">
          {isLoading ? (
            <div className="search-placeholder">
              Recherche en cours...
            </div>
          ) : searchTerm.length > 0 ? (
            <div className="search-results">
              {spots.length > 0 || events.length > 0 ? (
                <>
                  {/* Spots Results */}
                  {spots.length > 0 && (
                    <div className="results-section">
                      <h4 className="results-title">Spots</h4>
                      {spots.map((result) => (
                        <div 
                          key={result.documentId} 
                          className="search-result-item"
                          onClick={() => handleResultClick('spot', result.documentId)}
                        >
                          <img 
                            src={result.images[0]?.url ? `${API_BASE_URL}${result.images[0].url}` : '/placeholder.jpg'}
                            alt={result.name} 
                          />
                          <div className="result-info">
                            <h3>{result.name}</h3>
                            <div className="result-meta">
                              <span className="category">{result.category.name}</span>
                              <span className="location">
                                <MapPin size={14} />
                                {result.city}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Events Results */}
                  {events.length > 0 && (
                    <div className="results-section">
                      <h4 className="results-title">Événements</h4>
                      {events.map((event) => (
                        <div 
                          key={event.documentId} 
                          className="search-result-item"
                          onClick={() => handleResultClick('evenement', event.documentId)}
                        >
                          <img 
                            src={event.images[0]?.url ? `${API_BASE_URL}${event.images[0].url}` : '/placeholder.jpg'}
                            alt={event.name} 
                          />
                          <div className="result-info">
                            <h3>{event.name}</h3>
                            <div className="result-meta">
                              <span className="date">
                                <Calendar size={14} />
                                {formatEventDate(event.startTime)}
                              </span>
                              <span className="time">
                                <Clock size={14} />
                                {formatEventTime(event.startTime)}
                              </span>
                              {event.venueName && (
                                <span className="location">
                                  <MapPin size={14} />
                                  {event.venueName}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="no-results">
                  Aucun résultat trouvé pour "{searchTerm}"
                </div>
              )}
            </div>
          ) : (
            <div className="search-placeholder">
              <p>Commencez à taper pour rechercher</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Search;